import type { SearchHit, SearchRecordType, SearchResult, SearchSort } from "@closer/shared";
import {
  SEARCH_RECORD_TYPE_NAMES,
  SEARCH_RECORD_TYPES,
  SEARCH_SORT_NAMES,
  SEARCH_SORTS,
  TENANT_TYPE_NAMES,
} from "@closer/shared";
import { Input, Modal } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { SearchIcon } from "../components/icons";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/authContext";
import { isEditableTarget, isSearchHotkey } from "../lib/hotkey";
import { tenantKey } from "../lib/tenantQuery";
import { SearchResults, secenekId } from "./SearchResults";

/**
 * Sorgunun çalışması için gereken en az uzunluk.
 *
 * ⚠ Sunucudaki `config/search.json` ile AYNI ürün kuralını anlatıyor ve burada tekrar
 * yazılı. İstemci sunucu konfigini okumuyor: okusaydı her tuş vuruşunda bir konfig
 * çağrısı ya da ikinci bir uç gerekirdi. Ayrışma riski var ve bilinçli — sunucu kısa
 * sorguyu zaten boş sonuçla karşılıyor, yani ayrışmanın en kötü hâli fazladan bir istek.
 */
const MIN_UZUNLUK = 2;

/**
 * Üst Bar Arama açıcısı (CLOS-100 `[061]`).
 *
 * ### ⚠ Kanonik ad yalnız "Arama"
 *
 * Kart açık: *"'Global Arama' kullanılmaz."* Düğme, başlık ve alan etiketi aynı terimi
 * taşıyor; bir test bunu sabitliyor.
 *
 * ### Neden `Modal`, `TopBarPanel` değil
 *
 * `[059]`'un ortak kabuğu masaüstünde `Popover` açıyor ve popover'da ODAK KAPANI YOK —
 * bilinçli bir karar, çünkü panel açıkken sayfa kullanılabilir kalmalı. Ama bu kart odak
 * kapanını AÇIKÇA istiyor (*"ESC, odak kapanı ve klavye kullanımı desteklenir"*). Aramaya
 * girildiğinde kullanıcı gerçekten aramaya girmiş olur; arkadaki sayfa erişilebilir
 * kalırsa klavye kullanıcısı iki bağlam arasında kaybolur. `Modal` bu çekirdeği zaten
 * taşıyor (ADR-0022), kopyası çıkarılmadı.
 *
 * ### ⚠ Açıcı SONUÇ ÜRETMEZ — SORAR
 *
 * CLOS-100'ün sınırı hâlâ geçerli: *"açıcı sonuç üretmez veya erişim filtresini atlamaz."*
 * CLOS-184 ile panel artık sonuç GÖSTERİYOR ama üretmiyor: tek kaynak `/api/search` ve
 * bütün süzme (tenant, permission, kayıt erişimi) orada. İstemcide hiçbir domain ucuna
 * çağrı yok — kart bunu ayrıca yasaklıyor: *"CLOS-184 domain kayıtlarını doğrudan
 * okumaz."*
 *
 * İstemci tarafında bir arama kurulsaydı `[079]` kayıt erişim filtreleri devreye girmeden
 * veri gösterilirdi.
 */
export function SearchOpener() {
  const { activeTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [terim, setTerim] = useState("");
  const alanRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!isSearchHotkey(event)) return;
      // Kullanıcı yazıyorsa kısayol SUSAR — kartın olumsuz kriteri (`lib/hotkey.ts`).
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      setOpen(true);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  /**
   * Odak açılışta arama alanına gidiyor. `Modal`'ın odak kapanı ilk odaklanabilir öğeye
   * geçiyor ama o öğe başlıktaki "Kapat" düğmesi olabilir; arama açılıp da imlecin
   * kutuda olmaması, kısayolun bütün kazancını götürürdü.
   */
  useEffect(() => {
    if (open) alanRef.current?.focus();
  }, [open]);

  /**
   * ⚠ Çalışma alanı değişirse yazılan terim SIFIRLANIR — CLOS-100'ün çalışma zamanı
   * koşulu: *"Çalışma alanı değişirse açık panel eski tenant bağlamını temizler."* Terim
   * önceki alanın bağlamında yazıldı; taşınması kullanıcıya başka bir alanda oraya ait
   * olmayan bir arama yaptırırdı. Bu karar AYNEN duruyor.
   *
   * ⚠ AMA "ilk yükleme" bir DEĞİŞİM DEĞİLDİR (CLOS-855). Aktif alan üyelik listesinden
   * türüyor ve o liste panel açıldıktan SONRA gelebiliyor; `yok → ilk-kimlik` geçişini de
   * sıfırlama saymak, kullanıcının o an yazdığı terimi siliyordu. Kullanıcı hiçbir yere
   * geçmemişti — tek yaptığı hızlı yazmaktı ve kutusu kendiliğinden boşalıyordu.
   *
   * Bu yüzden ÖNCEKİ kimlik ayrıca tutuluyor: sıfırlama yalnız önceki kimlik DOLUYKEN
   * başka bir kimliğe geçilince koşuyor. Bağımlılık dizisi tek başına bu ikisini
   * ayırt edemez; `undefined` da bir "değer" gibi görünür.
   */
  const aktifId = activeTenant?.tenantId ?? null;
  const oncekiAktifId = useRef<string | null>(aktifId);
  useEffect(() => {
    const onceki = oncekiAktifId.current;
    oncekiAktifId.current = aktifId;
    if (onceki === null || onceki === aktifId) return;
    setTerim("");
  }, [aktifId]);

  /** Klavye gezinimindeki seçili sıra. −1 = seçim yok (kullanıcı henüz ok tuşuna basmadı). */
  const [seciliIndeks, setSeciliIndeks] = useState(-1);
  /**
   * Tür filtresi (CLOS-185 `[167]`). `null` = tüm izinli türler.
   *
   * ⚠ Filtre SUNUCUYA gidiyor, istemcide süzülmüyor. İstemcide süzseydik, tavana takılan
   * bir listenin görünmeyen kısmındaki eşleşmeler kaybolurdu: kullanıcı daralttığını
   * sanarken sonuç eksilirdi.
   */
  const [turFiltresi, setTurFiltresi] = useState<SearchRecordType | null>(null);
  const [siralama, setSiralama] = useState<SearchSort>("relevance");
  const navigate = useNavigate();

  const arananTerim = terim.trim();
  const yeterli = arananTerim.length >= MIN_UZUNLUK;

  /**
   * ⚠ ANAHTAR `tenantKey` İLE — ADR-0022 §2 ve CLOS-849'un bekçisi.
   *
   * Düz bir anahtar kullansaydık iki çalışma alanı aynı önbellek satırını paylaşırdı:
   * kullanıcı alan değiştirdiğinde önceki alanın arama sonuçlarını görürdü ve
   * `clearTenantQueries` o anahtarı tanımadığı için temizleyemezdi.
   */
  const anahtar = tenantKey(aktifId ?? "", "search", arananTerim, turFiltresi ?? "hepsi", siralama);

  const sorgu = useQuery({
    queryKey: anahtar,
    queryFn: async () => {
      const p = new URLSearchParams({ q: arananTerim, sort: siralama });
      if (turFiltresi) p.set("type", turFiltresi);
      return (
        await api<SearchResult>(`/api/search?${p.toString()}`, {
          tenantId: aktifId ?? undefined,
        })
      ).data;
    },
    /** Panel kapalıyken, alan seçili değilken ya da terim kısayken sorgu KOŞMUYOR. */
    enabled: open && Boolean(aktifId) && yeterli,
  });

  /** Gruplanmış sonuçların düz sırası — ok tuşları bu sırayı geziyor. */
  const duzListe = useMemo<SearchHit[]>(() => {
    const hits = sorgu.data?.hits ?? [];
    const sira = ["contract", "client", "template"];
    return [...hits].sort((a, b) => sira.indexOf(a.recordType) - sira.indexOf(b.recordType));
  }, [sorgu.data]);

  /**
   * ⚠ Terim değişince seçim SIFIRLANIYOR. Kalsaydı, kullanıcı üçüncü sonucu seçip yazmaya
   * devam ettiğinde imleç artık başka bir kayda işaret ediyor olurdu ve Enter yanlış kaydı
   * açardı — sessiz ve geri alınamaz bir yanlış yönlendirme.
   */
  useEffect(() => {
    setSeciliIndeks(-1);
  }, [arananTerim, turFiltresi, siralama]);

  function git(hit: SearchHit): void {
    setOpen(false);
    setTerim("");
    navigate(hit.href);
  }

  /**
   * Klavye gezinimi (kartın kriteri: *"klavye ve mobil gezinim desteklenir"*).
   *
   * ⚠ Ok tuşları alanın imlecini oynatmıyor (`preventDefault`): oynatsaydı kullanıcı hem
   * listede hem metinde geziniyor olurdu ve hangisinin hareket ettiği belirsizleşirdi.
   */
  function alanKlavye(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (duzListe.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSeciliIndeks((o) => (o + 1) % duzListe.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setSeciliIndeks((o) => (o <= 0 ? duzListe.length - 1 : o - 1));
      return;
    }
    if (event.key === "Enter" && seciliIndeks >= 0) {
      event.preventDefault();
      git(duzListe[seciliIndeks]!);
    }
  }

  return (
    <>
      {/**
       * ⚠ Açıcı bir ARAMA ALANI GİBİ görünüyor ama `<button>`; içine yazılmıyor.
       *
       * Onaylı tasarımda üst barın ortasında geniş bir arama kutusu var. Gerçek bir
       * `<input>` koymak iki kutu doğururdu: buradaki ve panelin içindeki — kullanıcı
       * üstte yazmaya başlar, panel açılınca yazdığı kaybolurdu. Görünüş alan, davranış
       * düğme; `aria-label` ile de ne olduğu doğru duyuruluyor.
       */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Arama"
        className="flex h-11 w-full items-center gap-2.5 rounded-xl border border-border-default bg-canvas px-3.5 text-left text-fg-faint transition-colors hover:border-border-strong hover:bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <SearchIcon className="size-[18px] shrink-0" />
        <span className="min-w-0 flex-1 truncate text-sm">Arama yapın…</span>
        {/* Kısayol ipucu yalnız geniş ekranda; dar ekranda simge tek başına yeterli. */}
        <span
          className="hidden rounded-md border border-border-default bg-surface px-1.5 py-0.5 text-[11px] font-medium text-fg-soft lg:inline"
          aria-hidden="true"
        >
          Ctrl+K
        </span>
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Arama">
        <div className="flex flex-col gap-3">
          {/* Kart: *"açıldığında aktif çalışma alanı açıkça görünür."* Kullanıcı hangi
              alanda aradığını bilmeden arama yapmamalı. */}
          {activeTenant ? (
            <p className="text-xs text-fg-soft">
              {activeTenant.name} · {TENANT_TYPE_NAMES[activeTenant.type]}
            </p>
          ) : null}

          <label htmlFor="arama-terimi" className="sr-only">
            Arama terimi
          </label>
          <Input
            id="arama-terimi"
            ref={alanRef}
            type="search"
            value={terim}
            onChange={(event) => setTerim(event.target.value)}
            placeholder="Sözleşme, şablon veya müvekkil ara"
            onKeyDown={alanKlavye}
            aria-controls="arama-sonuclari"
            /**
             * ⚠ ETKİN SEÇENEK SESLİ DE BİLDİRİLİYOR (CLOS-895 [S02]).
             *
             * Odak arama alanında kalıyor; seçili satırdaki `aria-selected` tek başına
             * ekran okuyucuya "şu an hangisi" demiyor. Bağ burada kuruluyor: ok tuşuyla
             * vurgu ilerlerken sesli takip de ilerliyor. Seçim yokken ÖZNİTELİK HİÇ
             * YAZILMIYOR — boş bir değer, var olmayan bir seçeneği işaret etmek olurdu.
             */
            aria-activedescendant={
              seciliIndeks >= 0 && duzListe[seciliIndeks]
                ? secenekId(duzListe[seciliIndeks]!)
                : undefined
            }
          />

          {/**
           * ⚠ FİLTRELER YALNIZ TERİM YETERLİYKEN ÇİZİLİYOR. Boş bir kutunun altında tür
           * seçenekleri göstermek, kullanıcının göremediği türlerin VAR olduğunu ele
           * verirdi — kart: *"filtre seçenekleri yetkisiz kayıt bilgisi sızdırmaz."*
           *
           * ⚠ Tür listesi SABİT KATALOGDAN geliyor, sonuçtan değil: sonuçtan türetseydik
           * "hangi türlerde kaydın var" bilgisi filtre çubuğundan okunurdu.
           *
           * ⚠ Buradaki kenarlık ve aşağıdaki seçili düğme rengi ÖLÜ SINIF taşıyordu:
           * `border-border` ve `text-on-primary` diye bir utility yok. Token eşlemesi
           * (`styles/index.css`) yalnız anlamsal adları açıyor; eşleşmeyen sınıf sessizce
           * hiçbir şey üretmiyordu. Sonuç: filtre şeridinin altındaki ayraç hiç
           * çizilmiyor, seçili filtre düğmesinin metni zeminiyle aynı renkte kalıyordu.
           */}
          {yeterli ? (
            <div className="flex flex-wrap items-center gap-2 border-b border-border-subtle pb-3">
              <div role="group" aria-label="Tür filtresi" className="flex flex-wrap gap-1">
                <FiltreDugmesi
                  secili={turFiltresi === null}
                  onClick={() => setTurFiltresi(null)}
                  etiket="Tümü"
                />
                {SEARCH_RECORD_TYPES.map((tur) => (
                  <FiltreDugmesi
                    key={tur}
                    secili={turFiltresi === tur}
                    onClick={() => setTurFiltresi(tur)}
                    etiket={SEARCH_RECORD_TYPE_NAMES[tur]}
                  />
                ))}
              </div>

              <label htmlFor="arama-siralama" className="sr-only">
                Sıralama
              </label>
              <select
                id="arama-siralama"
                value={siralama}
                onChange={(e) => setSiralama(e.target.value as SearchSort)}
                className="ml-auto rounded-lg border border-border-default bg-surface px-2.5 py-1.5 text-xs text-fg-strong"
              >
                {SEARCH_SORTS.map((s) => (
                  <option key={s} value={s}>
                    {SEARCH_SORT_NAMES[s]}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div id="arama-sonuclari" role="listbox" aria-label="Arama sonuçları">
            <SearchResults
              terim={terim}
              minUzunluk={MIN_UZUNLUK}
              durum={{
                data: sorgu.data,
                /** Sorgu hiç koşmadıysa `isPending` true kalır; "aranıyor" yazmamalı. */
                isPending: sorgu.isPending && sorgu.fetchStatus !== "idle",
                isError: sorgu.isError,
                error: sorgu.error,
                refetch: () => void sorgu.refetch(),
              }}
              onGit={git}
              seciliIndeks={seciliIndeks}
              duzListe={duzListe}
              turFiltresi={turFiltresi}
              onFiltreTemizle={() => setTurFiltresi(null)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

/**
 * Filtre düğmesi — seçili durumu `aria-pressed` ile bildiriliyor.
 *
 * ⚠ Görsel vurgu tek başına ekran okuyucuya hiçbir şey söylemez; kartın klavye/erişilebilirlik
 * beklentisi yalnız görenler için karşılanmış olurdu.
 */
function FiltreDugmesi({
  secili,
  onClick,
  etiket,
}: {
  secili: boolean;
  onClick: () => void;
  etiket: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={secili}
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
        secili ? "bg-primary text-on-inverse" : "bg-muted text-fg-soft hover:text-fg-strong"
      }`}
    >
      {etiket}
    </button>
  );
}
